import { useId, useRef, type ChangeEvent } from 'react';

interface Props {
  label: string;
  kind: 'logo' | 'favicon';
  help: string;
  preview?: string | null;
  filename?: string;
  changed: boolean;
  disabled?: boolean;
  onChoose: (event: ChangeEvent<HTMLInputElement>) => void;
  onRemove: () => void;
  onUndo: () => void;
}

/** File inputs cannot represent a persisted asset. Keep selection and saved
 * state explicit, using real shared buttons instead of the native filename UI. */
export function AssetUploadField({ label, kind, help, preview, filename, changed, disabled, onChoose, onRemove, onUndo }: Props) {
  const id = useId();
  const input = useRef<HTMLInputElement>(null);
  const removed = changed && !preview;
  const status = filename ? `${filename} · not saved yet`
    : removed ? `${kind === 'logo' ? 'Logo' : 'Favicon'} will be removed when you save.`
      : preview ? `${kind === 'logo' ? 'Logo' : 'Favicon'} saved.` : `No custom ${kind} saved.`;
  return <div className="asset-upload-field">
    <label htmlFor={id}>{label}</label>
    <input ref={input} id={id} className="sr-only" type="file" accept="image/png" tabIndex={-1} aria-describedby={`${id}-help`} disabled={disabled} onChange={onChoose} />
    <div className="asset-upload-field__row">
      {preview && <img className="asset-upload-field__preview" src={preview} alt="" />}
      <span className="asset-upload-field__status" role="status">{status}</span>
      <div className="asset-upload-field__actions">
        <button className="button button--secondary" type="button" aria-label={`${preview ? 'Replace' : 'Choose'} ${kind}`} disabled={disabled} onClick={() => input.current?.click()}>{preview ? 'Replace' : 'Choose PNG'}</button>
        {preview && <button className="button button--ghost" type="button" disabled={disabled} onClick={onRemove}>Remove {kind}</button>}
        {changed && <button className="button button--ghost" type="button" disabled={disabled} onClick={onUndo}>Undo {kind} change</button>}
      </div>
    </div>
    <small id={`${id}-help`} className="form-help">{help}</small>
  </div>;
}
